import React, { createContext, useContext } from 'react';
import { useSite, useLogFrontendApiError } from '@edreamsodigeo/frontend-core';
import useUserInfoQuery from 'clientState/hooks/useUserInfoQuery';

const PrimeContext = createContext();

const isUKSite = (site) => ['UK', 'OPUK'].includes(site);
const isUSSite = (site) => ['US'].includes(site);
const isNLSite = (site) => ['NL', 'OPNL'].includes(site);
const isFRSite = (site) => ['FR', 'OPFR'].includes(site);

const equalProminentPrimeDisplaySites = [
  'FR',
  'OPFR',
  'ES',
  'US',
  'IT',
  'OPIT',
  'NL',
  'OPNL',
  'OPES',
];
const itSites = ['IT', 'OPIT'];

const ddppCompliantSites = ['GOFR', 'FR', 'OPFR', 'NL', 'OPNL'];

const isItDisplaySite = (site) => itSites.includes(site);
const isEqualProminentPrimeDisplaySite = (site) =>
  equalProminentPrimeDisplaySites.includes(site);

const PrimeProvider = ({
  primeSitePropertySuccess,
  primeSiteProperty,
  userDataSuccess,
  dataPrimeUser,
  children,
  offers,
}) => {
  const site = useSite();
  const userQueryResult = useUserInfoQuery();
  const {
    data: userInfoData,
    loading: userInfoLoading,
    error: userInfoError,
  } = userQueryResult;
  useLogFrontendApiError(userQueryResult);

  const isSucceed =
    primeSitePropertySuccess &&
    !userInfoLoading &&
    !userInfoError &&
    userDataSuccess;

  const isPossibleReturningPrimeVisitor =
    primeSitePropertySuccess &&
    primeSiteProperty?.primeSiteProperties?.isPossibleReturningPrimeVisitor;
  const isLoggedPrimeUser =
    dataPrimeUser?.membershipAccountProperties.isActiveLogged ?? false;
  const isPrimeUser =
    isSucceed && (isLoggedPrimeUser || isPossibleReturningPrimeVisitor);
  const isPrimeSite =
    isSucceed &&
    primeSiteProperty?.primeSiteProperties?.activePrimeMarket?.flights;
  const isUK = isPrimeSite && isUKSite(site);
  const isUS = isPrimeSite && isUSSite(site);
  const isNL = isPrimeSite && isNLSite(site);
  const isFR = isPrimeSite && isFRSite(site);
  const isDDPPCompliant = isPrimeSite && ddppCompliantSites.includes(site);
  const isPrimeTotalPriceOnCard = isDDPPCompliant;
  const isPrimeFooter = isSucceed && isPrimeSite && !isPrimeUser;

  const userInfoSuccess = !userInfoLoading && !userInfoError;
  const isUserLogged = userInfoSuccess && !!userInfoData?.userInfo?.name;
  const isFencedScope = isUserLogged || isPossibleReturningPrimeVisitor;
  const hasTransparentDisplay = isPrimeSite && !isPrimeUser;
  const value = {
    isPrimeUser,
    isPrimeSite,
    isUserLogged,
    isUK,
    isNL,
    isFR,
    isUS,
    isITDisplay: isItDisplaySite(site),
    isPrimeTotalPriceOnCard,
    isPrimeFooter,
    isDefaultPrimeFooter: isPrimeFooter && !isItDisplaySite(site),
    isItalyPrimeFooter: isPrimeFooter && isItDisplaySite(site),
    isOpodoGermanyPrimeFooter: isPrimeFooter && site === 'OPDE',
    isDDPPCompliantDisplay: isDDPPCompliant,
    isPossibleReturningPrimeVisitor,
    isLoggedPrimeUser,
    isFencedScope,
    hasTransparentDisplay,
    isEqualProminentPrimeDisplayDefaultPrimeFooter:
      isPrimeFooter && !isEqualProminentPrimeDisplaySite(site),
    isEqualProminentPrimeDisplay: isEqualProminentPrimeDisplaySite(site),
    offers,
  };

  return (
    <PrimeContext.Provider value={value}>{children}</PrimeContext.Provider>
  );
};

export const usePrime = () => useContext(PrimeContext);

export default PrimeProvider;
